import { useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DialogContent, DialogDescription, DialogTitle, Dialog, DialogBody, DialogHeader } from "@/app/components/common/ui/dialog";
import { formatIsoDate } from '@/app/utils/Date';
import {
    DataGrid,
    DataGridColumnHeader,
} from '../../common/data-grid';
import { ColumnDef } from '@tanstack/react-table';
import { useLanguage } from '@/i18n';
import { PackageInnerView } from './PackageInnerView';
import { KeenIcon } from '../../common/keenicons';
import { ThemeOptionsData } from './ThemeOptionsData';

const PackageView = ({ open, onOpenChange, details }: any) => {
    const parentRef = useRef<any | null>(null);
    const { currentLanguage } = useLanguage();
    const { formatMessage } = useIntl();
    const [data, setData] = useState(null);
    const [innerViewModalOpen, setInnerViewModalOpen] = useState(false);
    const handleViewModalClose = () => {
        setInnerViewModalOpen(false);
        setData(null)
        setThemeoptionOpen(false);
        setthemeoptionData(null)
    };
    const [themeoptionData, setthemeoptionData] = useState(null);
    const [themeoptionOpen, setThemeoptionOpen] = useState(false);

    const parsedElementData = useMemo(() => {
        try {
            return Array.isArray(details?.data)
                ? details.data
                : JSON.parse(details?.data || '[]');
        } catch (e) {
            console.error("Failed to parse details.data", e);
            return [];
        }
    }, [details?.data]);

    const parsedthemeoptionData = useMemo(() => {
        try {
            return Array.isArray(details?.themeoption)
                ? details.themeoption
                : JSON.parse(details?.themeoption || '[]');
        } catch (e) {
            console.error("Failed to parse details.data", e);
            return [];
        }
    }, [details?.themeoption]);

    const themeOptionsData = useMemo(() => {
        if (!parsedthemeoptionData || typeof parsedthemeoptionData !== 'object') return [];

        const options = [
            { id: 'header', label: 'HEADER', value: parsedthemeoptionData.header },
            { id: 'footer', label: 'FOOTER', value: parsedthemeoptionData.footer },
            { id: 'custom_css', label: 'CUSTOM_CSS', value: parsedthemeoptionData.custom_css },
            { id: 'header_custom_js', label: 'HEADER_CUSTOM_JS', value: parsedthemeoptionData.header_custom_js },
            { id: 'footer_custom_js', label: 'FOOTER_CUSTOM_JS', value: parsedthemeoptionData.footer_custom_js },
        ];

        // Only return options with meaningful data
        return options.filter(option => {
            const val = option.value;
            // Case: null or empty string
            if (val === null || val === '' || val === undefined) return false;
            // Case: object or array but all values are empty
            if (typeof val === 'object') {
                if (Array.isArray(val)) return val.length > 0;
                // object - check if any value inside is not null/empty/false
                return Object.values(val).some(inner => {
                    if (Array.isArray(inner)) return inner.length > 0;
                    return inner !== null && inner !== '' && inner !== undefined;
                });
            }
            return true;
        });
    }, [parsedthemeoptionData]);


    const columns = useMemo<ColumnDef<any>[]>(
        () => [
            {
                accessorFn: (row) => row.index,
                id: 'index',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: 'ID', defaultMessage: 'ID' })}
                        column={column}
                    />
                ),
                enableSorting: false,
                enableHiding: false,
                cell: (info) => {
                    return (
                        <span
                            className="leading-none font-medium text-sm text-gray-900"
                        >
                            {info.row.index + 1}
                        </span>
                    )
                },
                meta: {
                    headerTitle: formatMessage({ id: 'ID', defaultMessage: 'ID' })
                },

            },
            {
                accessorFn: (row) => row.title,
                id: 'title',
                header: ({ column }) => <DataGridColumnHeader title={formatMessage({ id: 'TITLE', defaultMessage: 'Title' })} column={column} />,
                enableSorting: false,
                enableHiding: false,
                cell: (info) => (
                    <div className='flex flex-col gap-2'>
                        <span
                            className="leading-none font-medium text-sm text-gray-900 hover:text-primary"
                        >
                            {info.row.original?.elementdata?.title}
                        </span>
                    </div>
                ),
                meta: {
                    headerTitle: formatMessage({ id: 'TITLE', defaultMessage: 'Title' }),
                    className: 'min-w-[135px]'
                }
            },

            {
                accessorFn: (row) => row.typo3version,
                id: 'typo3version',
                header: ({ column }) => <DataGridColumnHeader title={formatMessage({ id: 'SHORT_TITLE', defaultMessage: 'Short Title' })} column={column} />,
                enableSorting: false,
                enableHiding: false,
                cell: (info) => info.row.original?.elementdata?.short_title,
                meta: {
                    headerTitle: formatMessage({ id: 'SHORT_TITLE', defaultMessage: 'Short Title' }),
                    className: 'min-w-[135px]'
                }
            },
            {
                accessorFn: (row) => row.key,
                id: 'key',
                header: ({ column }) => <DataGridColumnHeader title={formatMessage({ id: 'KEY', defaultMessage: 'Key' })} column={column} />,
                enableSorting: false,
                enableHiding: false,
                cell: (info) => (
                    <div className="flex flex-wrap gap-2.5 mb-2">
                        <span
                            className="badge badge-sm badge-gray-200"
                        >
                            {info.row.original.elementdata?.key ?? "-"}
                        </span>
                    </div>
                ),
                meta: {
                    headerTitle: formatMessage({ id: 'KEY', defaultMessage: 'Key' }),
                    className: 'min-w-[135px]'
                }
            },

            {
                id: 'view',
                header: () => '',
                enableSorting: false,
                cell: (info) => (
                    <span
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={async (e) => {
                            setData(info.row.original);
                            setInnerViewModalOpen(true);
                        }}
                    >
                        <KeenIcon icon="eye" />
                    </span>
                ),
                meta: {
                    headerClassName: 'w-[60px]',
                },
            },

        ],
        [formatMessage, currentLanguage]
    );

    const themeoptionlist = useMemo<ColumnDef<any>[]>(
        () => [
            {
                accessorFn: (row) => row.index,
                id: 'index',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: 'ID', defaultMessage: 'ID' })}
                        column={column}
                    />
                ),
                enableSorting: false,
                enableHiding: false,
                cell: (info) => {
                    return (
                        <span
                            className="leading-none font-medium text-sm text-gray-900"
                        >
                            {info.row.index + 1}
                        </span>
                    )
                },
                meta: {
                    headerTitle: formatMessage({ id: 'ID', defaultMessage: 'ID' })
                },

            },
            {
                accessorFn: (row) => row.label,
                id: 'label',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: 'TYPE', defaultMessage: 'Type' })}
                        column={column}
                    />
                ),
                enableSorting: false,
                enableHiding: false,
                cell: (info) => (
                    <span className="leading-none font-medium text-sm text-gray-900">
                        <FormattedMessage id={info.row.original?.label} />
                    </span>
                ),
                meta: {
                    headerTitle: formatMessage({ id: 'TYPE', defaultMessage: 'Type' }),
                    className: 'min-w-[135px]'
                }
            },
            {
                id: 'view',
                header: () => '',
                enableSorting: false,
                cell: (info) => (
                    <span
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={async (e) => {
                            setthemeoptionData(info.row.original);
                            setThemeoptionOpen(true);
                        }}
                    >
                        <KeenIcon icon="eye" />
                    </span>
                ),
                meta: {
                    headerClassName: 'w-[60px]',
                },
            },

        ],
        [formatMessage, currentLanguage]
    );

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="container-fixed max-w-[auto] flex flex-col p-10 overflow-hidden [&>button]:hidden">
                    <DialogHeader className="p-0 border-0">
                        <DialogTitle></DialogTitle>
                        <DialogDescription></DialogDescription>
                        <div className="flex items-center justify-between flex-wrap grow gap-5 pb-7.5">
                            <div className="flex flex-col justify-center gap-2">
                                <h1 className="text-xl font-semibold leading-none text-gray-900"><FormattedMessage id="PACKAGE_DETAILS" /></h1>
                            </div>
                            <button className="btn btn-sm btn-light" onClick={onOpenChange}>
                                <FormattedMessage id="CLOSE" />
                            </button>
                        </div>
                    </DialogHeader>
                    <DialogBody className="scrollable-y py-0 mb-5 ps-0 pe-3 -me-7" ref={parentRef}>
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
                            {
                                details ?
                                    <>
                                        <div className="col-span-1">
                                            <div className="flex flex-col gap-5 lg:gap-7.5 sticky top-0">
                                                <div className="card min-w-full">
                                                    <div className="card-header">
                                                        <h3 className="card-title"><FormattedMessage id="BASIC_DETAILS" /></h3>
                                                    </div>
                                                    <div className="card-table scrollable-x-auto pb-3">
                                                        {
                                                            details?.name || details?.first_name || details?.email || details?.typo3version || details?.created_at || details?.description ?
                                                                <div className="flex items-baseline flex-wrap lg:flex-nowrap gap-2.5 ">
                                                                    <table className="table align-middle text-sm text-gray-500">
                                                                        <tbody>
                                                                            <tr>
                                                                                <td className="text-gray-600 font-normal"><FormattedMessage id="NAME" /></td>
                                                                                <td className="text-gray-800 font-normal">
                                                                                    {details?.name ?? "-"}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="text-gray-600 font-normal"><FormattedMessage id="TYPO3_VERSION" /></td>
                                                                                <td className="text-gray-800 font-normal">
                                                                                    {details?.typo3version ?? "-"}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="text-gray-600 font-normal"><FormattedMessage id="CREATED" /></td>
                                                                                <td className="text-gray-800 font-normal">
                                                                                    {details?.created_at ? formatIsoDate(details.created_at) : "-"}
                                                                                </td>
                                                                            </tr>
                                                                            {
                                                                                details?.description ?
                                                                                    <tr>
                                                                                        <td className="text-gray-600 font-normal"><FormattedMessage id="DESCRIPTION" /></td>
                                                                                        <td className="text-gray-800 font-normal" dangerouslySetInnerHTML={{ __html: details?.description }}>
                                                                                        </td>
                                                                                    </tr>
                                                                                    : null
                                                                            }
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                                : <div className="card-header border-[0px]"><span className='text-gray-800 font-normal'><FormattedMessage id="NOT_FOUND_PACKAGE_BASIC_DETAIL" /></span></div>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-span-2 flex flex-wrap gap-6 flex-col">
                                            <div className="flex flex-col gap-5 lg:gap-7.5">
                                                <div className="card min-w-full">
                                                    <div className="card-header">
                                                        <h3 className="card-title"><FormattedMessage id="ELEMENTS_LIST" /></h3>
                                                    </div>
                                                    <div className="card-table scrollable-x-auto">
                                                        <DataGrid
                                                            columns={columns}
                                                            data={parsedElementData}
                                                            rowSelection={true}
                                                            getRowId={(row: any) => row.id}
                                                            pagination={{ size: parsedElementData.length ?? 0 }}
                                                            layout={{
                                                                card: false,
                                                                cellBorder: false,
                                                                classes: {
                                                                    container: 'border-[0px] d-none-data-pagination',
                                                                },
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>


                                            <div className="flex flex-col gap-5 lg:gap-7.5">
                                                <div className="card min-w-full">
                                                    <div className="card-header">
                                                        <h3 className="card-title">
                                                            <FormattedMessage id="THEME_OPTIONS" />
                                                        </h3>
                                                    </div>
                                                    <div className="card-table scrollable-x-auto">
                                                        <DataGrid
                                                            columns={themeoptionlist}
                                                            data={themeOptionsData}
                                                            rowSelection={true}
                                                            getRowId={(row: any) => row.id}
                                                            pagination={{ size: themeOptionsData.length ?? 0 }}
                                                            layout={{
                                                                card: false,
                                                                cellBorder: false,
                                                                classes: {
                                                                    container: 'border-[0px] d-none-data-pagination',
                                                                },
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                    :
                                    <div className="col-span-1">
                                        <div className="flex flex-col gap-5 lg:gap-7.5">
                                            <div className="card min-w-full">
                                                <div className="card-header border-[0px]">
                                                    <span className='text-gray-800 font-normal'><FormattedMessage id="NOT_FOUND_PACKAGE_DETAILS" /></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                            }
                        </div>
                    </DialogBody>
                </DialogContent >
            </Dialog>
            {innerViewModalOpen && <PackageInnerView open={innerViewModalOpen} onOpenChange={handleViewModalClose} data={data} />}

            {themeoptionOpen && <ThemeOptionsData open={themeoptionOpen} onOpenChange={handleViewModalClose} data={themeoptionData} />}
        </>
    );
};

export { PackageView };
